import { formatDateToShortDate } from "@/utils/formatter";
import { CommentFilters, CommentStatus, CommentWatchlist } from "./comments";
import { calculateDateInterval } from "@/utils/dateTime";

export type Folder = { name: string; filters: CommentFilters };
export type Watchlist = Folder & { id: number; isPublic: boolean };

export type Features = {
  comments?: {
    comments: boolean;
    folders?: Folder[];
    watchlists?: Watchlist[];
  };
  dms?: boolean;
  posts?: {
    postlist: boolean;
    folders?: Folder[];
    watchlists?: Watchlist[];
  };
  userList?: {
    userlist: boolean;
    folders?: Folder[];
    watchlists?: Watchlist[];
  };

  tweets?: boolean;
  threads?: {
    threads: boolean;
    folders?: Folder[];
    watchlists?: Watchlist[];
  };
};

export enum PlatformKey {
  INSTAGRAM = "instagram",
  FACEBOOK = "facebook",
  TWITTER = "twitter",
  YOUTUBE = "youtube",
  TELEGRAM = "telegram",
  IMESSAGE = "imessage",
  NOTIFY = "notify",
  TIKTOK = "tiktok",
}

export type Platform = {
  id: SocialMediaPlatform;
  key: PlatformKey;
  label: string;
  features: Features;
  baseUrl?: string;
};

export enum SocialMediaPlatform {
  INSTAGRAM = 1,
  FACEBOOK = 2,
  YOUTUBE = 3,
  TWITTER = 4,
  TELEGRAM = 5,
  IMESSAGE = 6,
  NOTIFY = 7,
  TIKTOK = 8,
}

const [defaultIntervalFrom, defaultIntervalTo] = calculateDateInterval(7).map((d) =>
  formatDateToShortDate(d, true, "-"),
);

const [defaultOpenCommentListIntervalFrom, defaultOpenCommentListIntervalTo] = calculateDateInterval(0).map((d) =>
  formatDateToShortDate(d, true, "-"),
);

const defaultCommentFolders: Folder[] = [
  {
    name: "open",
    filters: {
      status: "open",
      from: defaultOpenCommentListIntervalFrom,
      to: defaultOpenCommentListIntervalTo,
    },
  },
  {
    name: "progress",
    filters: {
      status: "progress",
      from: defaultIntervalFrom,
      to: defaultIntervalTo,
    },
  },
  {
    name: "done",
    filters: {
      status: "done",
      from: defaultIntervalFrom,
      to: defaultIntervalTo,
    },
  },
];

const defaultUserlistFolders = [
  {
    name: "active",
    filters: { from: defaultIntervalFrom, to: defaultIntervalTo },
  },
  {
    name: "blocked",
    filters: {
      blockStates: ["blocked"],
      commentCount: true,
    },
  },
  {
    name: "all",
    filters: {},
  },
];

const defaultPostlistFolders = [
  {
    name: "open",
    filters: {
      statuses: ["pending"],
      visibilities: ["visible"],
      statusFilter: "any",
    },
  },
  // {
  //   name: "progress",
  //   filters: {
  //     statuses: ["progress"],
  //     statusConnection: "or",
  //     statusFilter: "any",
  //   },
  // },
  {
    name: "done",
    filters: {
      statuses: ["accepted", "deleted"],
      statusFilter: "all",
      visibilities: ["notVisible"],
      statusConnection: "or",
    },
  },
  {
    name: "all",
    filters: {},
  },
];

export const getPlatforms = () => {
  const platforms: Platform[] = [
    {
      id: SocialMediaPlatform.INSTAGRAM,
      label: "Instagram",
      key: PlatformKey.INSTAGRAM,
      features: {
        comments: {
          comments: true,
          folders: [...defaultCommentFolders],
          watchlists: [],
        },
        dms: true,
        posts: {
          postlist: true,
          folders: [...defaultPostlistFolders],
        },
        userList: {
          userlist: true,
          folders: [...defaultUserlistFolders],
        },
      },
      baseUrl: "https://www.instagram.com",
    },
    {
      id: SocialMediaPlatform.FACEBOOK,
      label: "Facebook",
      key: PlatformKey.FACEBOOK,
      features: {
        comments: {
          comments: true,
          folders: [...defaultCommentFolders],
          watchlists: [],
        },
        dms: true,
        posts: {
          postlist: true,
          folders: [...defaultPostlistFolders],
        },
        userList: {
          userlist: true,
          folders: [...defaultUserlistFolders],
        },
      },
    },
    {
      id: SocialMediaPlatform.YOUTUBE,
      label: "Youtube",
      key: PlatformKey.YOUTUBE,
      features: {
        comments: {
          comments: true,
          folders: [...defaultCommentFolders],
          watchlists: [],
        },
        dms: false,
        posts: {
          postlist: true,
          folders: [...defaultPostlistFolders],
        },
        userList: {
          userlist: true,
          folders: [...defaultUserlistFolders],
        },
      },
    },
    {
      id: SocialMediaPlatform.TWITTER,
      label: "Twitter",
      key: PlatformKey.TWITTER,
      features: {
        tweets: true,
      },
    },
    {
      id: SocialMediaPlatform.TELEGRAM,
      label: "Telegram",
      key: PlatformKey.TELEGRAM,
      features: {
        comments: {
          comments: true,
          folders: [...defaultCommentFolders],
        },
        dms: true,
        posts: {
          postlist: true,
          folders: [...defaultPostlistFolders],
        },
        userList: {
          userlist: true,
          folders: [...defaultUserlistFolders],
        },
      },
    },
    {
      id: SocialMediaPlatform.IMESSAGE,
      label: "Apple iMessage",
      key: PlatformKey.IMESSAGE,
      features: {
        comments: {
          comments: true,
          folders: [...defaultCommentFolders],
        },
        dms: true,
        posts: {
          postlist: true,
          folders: [...defaultPostlistFolders],
        },
        userList: {
          userlist: true,
          folders: [...defaultUserlistFolders],
        },
      },
    },
    {
      id: SocialMediaPlatform.NOTIFY,
      label: "Notify",
      key: PlatformKey.NOTIFY,
      features: {
        comments: {
          comments: false,
          folders: [],
        },
        dms: true,
        posts: {
          postlist: true,
          folders: [...defaultPostlistFolders],
        },
        userList: {
          userlist: true,
          folders: [...defaultUserlistFolders],
        },
      },
    },
    {
      id: SocialMediaPlatform.TIKTOK,
      label: "Tiktok",
      key: PlatformKey.TIKTOK,
      features: {
        comments: {
          comments: true,
          folders: [...defaultCommentFolders],
          watchlists: [],
        },
        dms: false,
        posts: {
          postlist: true,
          folders: [...defaultPostlistFolders],
        },
        userList: {
          userlist: true,
          folders: [...defaultUserlistFolders],
        },
      },
    },
  ];

  return platforms;
};
